import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, ScrollView, Alert } from 'react-native';
import { Button, Icon } from 'react-native-elements';
import * as Speech from 'expo-speech';
import { Audio } from 'expo-av';
import { useAuth } from '../context/AuthContext';
import { soundService } from '../services/soundService';
import wordsData from '../data/words.json';
import { supabase } from '../lib/supabase';
import { useNavigation, ParamListBase } from '@react-navigation/native';
import { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';

declare global {
  interface Window {
    webkitSpeechRecognition: any;
  }
}

const testWordComparison = (transcript: string, currentWord: string) => {
  const cleanTranscript = transcript.toLowerCase().trim().replace(/[.,!?]/g, '').split(' ');
  const cleanCurrentWord = currentWord.toLowerCase().trim();
  const isCorrect = cleanTranscript.some(word => word === cleanCurrentWord);
  
  console.log('Test Comparison:', {
    originalTranscript: transcript,
    cleanedWords: cleanTranscript,
    currentWord: currentWord,
    cleanedCurrentWord: cleanCurrentWord,
    isCorrect: isCorrect
  });
  return isCorrect;
};

export default function TrainingModeScreen() {
  const { user } = useAuth();
  const [currentWord, setCurrentWord] = useState('');
  const currentWordRef = useRef(currentWord);
  const [selectedGrade, setSelectedGrade] = useState("grade1");
  const [selectedDifficulty, setSelectedDifficulty] = useState("beginner");
  const [isListening, setIsListening] = useState(false);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const isProcessingRef = useRef(false);
  const recognitionRef = useRef<any>(null);
  const [recognizedText, setRecognizedText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [wordsPerSession, setWordsPerSession] = useState(5);
  const [attemptsAllowed, setAttemptsAllowed] = useState(3);
  const [wordsRemaining, setWordsRemaining] = useState(wordsPerSession);
  const [attempts, setAttempts] = useState(0);
  const attemptsRef = useRef(0);
  const [wordCompleted, setWordCompleted] = useState(false);
  const [sessionResults, setSessionResults] = useState<Array<{
    word: string;
    attempts: number;
    success: boolean;
    transcript: string;
  }>>([]);
  const sessionResultsRef = useRef(sessionResults);
  const [isSessionComplete, setIsSessionComplete] = useState(false);
  const [sessionSent, setSessionSent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const startTimeRef = useRef(Date.now());
  const [struggledWords, setStruggleWords] = useState<string[]>([]);
  const [missedWords, setMissedWords] = useState<string[]>([]);
  const [attemptsPerWord, setAttemptsPerWord] = useState<Record<string, number>>({});
  const navigation = useNavigation<BottomTabNavigationProp<ParamListBase>>();
  const [attemptsTimestamps, setAttemptsTimestamps] = useState<Array<{
    word: string;
    timestamp: string;
    success: boolean;
    attempt: number;
    transcript: string;
  }>>([]);
  const [sessionData, setSessionData] = useState<any>(null);

  useEffect(() => {
    sessionResultsRef.current = sessionResults;
  }, [sessionResults]);

  // Whenever wordsPerSession changes (via settings), update wordsRemaining accordingly.
  useEffect(() => {
    setWordsRemaining(wordsPerSession);
  }, [wordsPerSession]);

  // Log state changes for debugging
  useEffect(() => {
    console.log("STATE UPDATE -- currentWord:", currentWord, "attempts:", attempts, "wordsRemaining:", wordsRemaining, "wordCompleted:", wordCompleted);
  }, [currentWord, attempts, wordsRemaining, wordCompleted]);

  useEffect(() => {
    currentWordRef.current = currentWord;
    console.log("Updated currentWordRef to:", currentWordRef.current);
  }, [currentWord]);

  const nextWord = useCallback(() => {
    if (isSessionComplete) return;
    
    // First, ensure the previous word is properly recorded
    if (currentWord) {
      const result = {
        word: currentWord,
        attempts: attemptsRef.current,
        success: wordCompleted,  // Use wordCompleted to determine success
        transcript: recognizedText
      };
      setSessionResults(prev => [...prev, result]);
      if (!wordCompleted) {
        setMissedWords(prev => [...prev, currentWord]);
      }
    }
    
    const availableWords = wordsData[selectedGrade][selectedDifficulty];
    let newWord;
    do {
      const randomIndex = Math.floor(Math.random() * availableWords.length);
      newWord = availableWords[randomIndex];
    } while (sessionResults.some(result => result.word === newWord));
    
    // Update words remaining after recording the previous word
    if (currentWord) {
      setWordsRemaining(prev => {
        const remaining = prev - 1;
        // Only finalize if we've completed all words
        if (remaining <= 0) {
          setTimeout(() => finalizeSession('failure'), 0);
          return 0;
        }
        return remaining;
      });
    }
    
    // Reset all state for the new word
    setCurrentWord(newWord);
    currentWordRef.current = newWord;
    setAttempts(0);
    attemptsRef.current = 0;
    setRecognizedText('');
    setInterimText('');
    setWordCompleted(false);
    
  }, [selectedGrade, selectedDifficulty, sessionResults, isSessionComplete, currentWord, wordCompleted, recognizedText]);

  const playSound = useCallback((type: 'correct' | 'incorrect') => {
    soundService.playSound(type);
  }, []);

  const processResult = useCallback((transcript: string) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    
    try {
      const isCorrect = testWordComparison(transcript, currentWordRef.current);
      const timestamp = new Date().toISOString();
      
      const newAttempts = attemptsRef.current + 1;
      setAttempts(newAttempts);
      attemptsRef.current = newAttempts;
      
      // Always track the attempt
      setAttemptsTimestamps(prev => [...prev, {
        word: currentWordRef.current,
        timestamp,
        success: isCorrect,
        attempt: newAttempts,
        transcript
      }]);
      
      if (isCorrect || newAttempts >= attemptsAllowed) {
        // Record the result before moving to next word
        const result = {
          word: currentWordRef.current,
          attempts: newAttempts,
          success: isCorrect,
          transcript: transcript
        };
        setSessionResults(prev => [...prev, result]);
        setWordCompleted(true);

        if (isCorrect) {
          playSound('correct');
          setScore(prev => prev + 10);
          setStreak(prev => prev + 1);
        } else {
          playSound('incorrect');
          setStreak(0);
        }

        // Decrement words remaining and check if session should end
        setWordsRemaining(prev => {
          const remaining = prev - 1;
          if (remaining <= 0) {
            setTimeout(() => finalizeSession(isCorrect ? 'success' : 'failure'), 0);
            return 0;
          }
          return remaining;
        });

        // Schedule next word
        setTimeout(() => nextWord(), 1000);
      } else {
        playSound('incorrect');
        setStreak(0);
      }
    } catch (error) {
      console.error('Error in processResult:', error);
    } finally {
      isProcessingRef.current = false;
    }
  }, [attempts, attemptsAllowed, nextWord, playSound]);

  const setupRecognition = useCallback(() => {
    try {
      const recog = new window.webkitSpeechRecognition();
      recog.continuous = true;
      recog.interimResults = true;
      recog.lang = 'en-US';
      
      recog.onstart = () => {
        console.log('Speech recognition started');
        setIsListening(true);
        setInterimText('Listening...');
      };

      recog.onresult = (event: any) => {
        const last = event.results.length - 1;
        const transcript = event.results[last][0].transcript;

        if (!event.results[last].isFinal) { 
          setInterimText(transcript);
          return;
        }

        setRecognizedText(transcript);
        processResult(transcript);
      };

      recog.onerror = (event: any) => {
        console.log('Speech recognition error:', event.error);
        if (event.error === 'no-speech') {
          setInterimText('No speech detected. Please try again.');
        }
        setIsListening(false);
      };

      recog.onend = () => {
        console.log('Speech recognition ended');
        if (!isSessionComplete) {
          try {
            if (isListening) {
              recog.start();
            }
          } catch (error) {
            console.error('Error restarting recognition:', error);
            setIsListening(false);
          }
        }
      };

      return recog;
    } catch (error) {
      console.error('Error setting up recognition:', error);
      return null;
    }
  }, [isSessionComplete, isListening, processResult]);

  const startListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.error('Error stopping recognition:', e);
      }
    }
    
    recognitionRef.current = setupRecognition();
    
    if (recognitionRef.current) {
      try {
        setIsListening(true);
        recognitionRef.current.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
        setIsListening(false);
      }
    } else {
      setInterimText('Speech recognition not supported in this browser');
    }
  }, [setupRecognition]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      console.log('Stopping speech recognition');
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error('Error stopping recognition:', error);
      }
      setIsListening(false);
    }
  }, []);

  const handleStartSession = () => {
    setSessionStarted(true);
    // Initialize first word and start listening
    const availableWords = wordsData[selectedGrade][selectedDifficulty];
    const randomIndex = Math.floor(Math.random() * availableWords.length);
    const firstWord = availableWords[randomIndex];
    
    setScore(0);
    setStreak(0);
    setAttempts(0);
    setSessionResults([]);
    setWordsRemaining(wordsPerSession);
    setIsSessionComplete(false);
    startTimeRef.current = Date.now();
    
    // Set the first word
    setCurrentWord(firstWord);
    currentWordRef.current = firstWord;
    attemptsRef.current = 0;
    setRecognizedText('');
    setInterimText('');
    setWordCompleted(false);
    
    // Start listening after a short delay
    setTimeout(() => {
      startListening();
    }, 100);
  };

  const speakWord = () => {
    Speech.speak(currentWord, {
      language: 'en',
      pitch: 1,
      rate: 0.75,
    });
  };

  const handleStopStart = () => {
    if (isListening) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
    } else {
      startListening();
      setIsListening(true);
    }
  };

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const handleSessionComplete = useCallback(async () => {
    if (sessionSent) return;
    setSessionSent(true);
    console.log('=== Starting Session Completion ===');
    
    // Use sessionResultsRef to ensure we have the latest results
    const finalResults = sessionResultsRef.current;
    console.log('Final results array:', finalResults);
  
    if (!finalResults?.length) {
      console.log('No session results to save');
      setIsSessionComplete(true);
      return;
    }

    const calculatedAttemptsPerWord = finalResults.reduce((acc, result) => {
      acc[result.word] = result.attempts;
      return acc;
    }, {} as Record<string, number>);

    const sessionData = {
      user_id: user?.id,
      grade: selectedGrade,
      difficulty: selectedDifficulty,
      total_words: finalResults.length,
      correct_words: finalResults.filter(r => r.success).length,
      total_attempts: attemptsRef.current,
      total_time_ms: Date.now() - startTimeRef.current,
      missed_words: finalResults.filter(r => !r.success).map(r => r.word),
      session_score: Math.round((finalResults.filter(r => r.success).length / finalResults.length) * 100),
      attempts_per_word: calculatedAttemptsPerWord,
      attempts_timestamps: attemptsTimestamps,
      date: new Date().toISOString(),
      session_type: 'training'
    };

    try {
      const { data, error } = await supabase
        .from('training_sessions')
        .insert(sessionData)
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      console.log('Session saved successfully:', data);
      setSessionData(data);
      setIsSessionComplete(true);
    } catch (error) {
      console.error('Failed to save session:', error.message || error);
      Alert.alert('Error', 'Failed to save session results');
    }
  }, [user, selectedGrade, selectedDifficulty, attemptsTimestamps, sessionResults]);

  // Add a new function to handle viewing progress
  const viewProgress = useCallback(() => {
    navigation.navigate('Progress', {
      refresh: Date.now(), // Pass a timestamp to force refresh
      lastSessionId: sessionData?.id // Pass the ID of the just-completed session
    });
  }, [navigation]);

  const handleStopSession = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
    setSessionStarted(false);
    handleSessionComplete();
  }, []);

  const finalizeSession = useCallback((resultType: 'success' | 'failure') => {
    if (isSessionComplete) return;
    setIsSessionComplete(true);
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
    // Ensure the last word is recorded
    if (currentWord && !wordCompleted) {
      setSessionResults(prev => [...prev, {
        word: currentWord,
        attempts: attemptsRef.current,
        success: false,
        transcript: recognizedText
      }]);
    }
    setTimeout(() => {
      handleSessionComplete();
    }, 1000);
  }, [isSessionComplete, currentWord, wordCompleted, attemptsRef.current, recognizedText]);

  const renderSessionSummary = () => (
    <View style={styles.sessionSummary}>
      <Text style={styles.summaryTitle}>Session Complete!</Text>
      <Text style={styles.summaryScore}>Final Score: {score}</Text>
      <Text style={styles.summaryText}>
        Words Attempted: {sessionResults.length}
      </Text>
      <Text style={styles.summaryText}>
        Words Correct: {sessionResults.filter(r => r.success).length}
      </Text>
      <Text style={styles.summaryText}>
        Accuracy: {Math.round((sessionResults.filter(r => r.success).length / sessionResults.length) * 100)}%
      </Text>
      <View style={styles.wordResultsContainer}>
        <Text style={styles.sectionTitle}>Word Results:</Text>
        {sessionResults.map((result, index) => (
          <Text key={index} style={[
            styles.wordResult,
            { color: result.success ? '#4CAF50' : '#F44336' }
          ]}>
            {result.word}: {result.success ? '✓' : '✗'} ({result.attempts} attempts)
          </Text>
        ))}
      </View>
      {missedWords.length > 0 && (
        <View style={styles.missedWordsContainer}>
          <Text style={styles.missedWordsTitle}>Words to Practice:</Text>
          <Text style={styles.missedWordsList}>{missedWords.join(', ')}</Text>
          <Text style={styles.missedWordsHint}>
            These words will be tracked in your progress to help focus future practice.
          </Text>
        </View>
      )}
      <View style={styles.buttonContainer}>
        <Button
          title="View Progress"
          onPress={viewProgress}
          buttonStyle={styles.progressButton}
        />
        <Button
          title="Start New Session"
          onPress={() => {
            setSessionStarted(false);
            setIsSessionComplete(false);
            setSessionSent(false);
          }}
          buttonStyle={styles.newSessionButton}
        />
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.contentWrapper}>
        { !sessionStarted && !isSessionComplete ? (
          <ScrollView contentContainerStyle={styles.splashScreen}>
            <View style={styles.splashContent}>
              <Text style={styles.splashTitle}>Training Mode Setup</Text>
              <Text style={styles.label}>Grade Level:</Text>
              
              <View style={styles.selectorContainer}>
                <Button 
                  title="Grade 1" 
                  onPress={() => setSelectedGrade('grade1')}
                  buttonStyle={selectedGrade === 'grade1' ? styles.selectedButton : styles.unselectedButton}
                />
                <Button 
                  title="Grade 2" 
                  onPress={() => setSelectedGrade('grade2')}
                  buttonStyle={selectedGrade === 'grade2' ? styles.selectedButton : styles.unselectedButton}
                />
                <Button 
                  title="Grade 3" 
                  onPress={() => setSelectedGrade('grade3')}
                  buttonStyle={selectedGrade === 'grade3' ? styles.selectedButton : styles.unselectedButton}
                />
                <Button 
                  title="Grade 4" 
                  onPress={() => setSelectedGrade('grade4')}
                  buttonStyle={selectedGrade === 'grade4' ? styles.selectedButton : styles.unselectedButton}
                />
                <Button 
                  title="Grade 5" 
                  onPress={() => setSelectedGrade('grade5')}
                  buttonStyle={selectedGrade === 'grade5' ? styles.selectedButton : styles.unselectedButton}
                />
              </View>

              <Text style={styles.label}>Difficulty:</Text>
              <View style={styles.selectorContainer}>
                <Button 
                  title="Beginner" 
                  onPress={() => setSelectedDifficulty('beginner')}
                  buttonStyle={selectedDifficulty === 'beginner' ? styles.selectedButton : styles.unselectedButton}
                />
                <Button 
                  title="Intermediate" 
                  onPress={() => setSelectedDifficulty('intermediate')}
                  buttonStyle={selectedDifficulty === 'intermediate' ? styles.selectedButton : styles.unselectedButton}
                />
                <Button 
                  title="Advanced" 
                  onPress={() => setSelectedDifficulty('advanced')}
                  buttonStyle={selectedDifficulty === 'advanced' ? styles.selectedButton : styles.unselectedButton}
                />
              </View>

              <View style={styles.numberSelectorRow}>
                <Text style={styles.label}>Max Attempts per Word: {attemptsAllowed}</Text>
                <TouchableOpacity onPress={() => setAttemptsAllowed(prev => Math.max(1, prev - 1))}>
                  <Text style={styles.numberButton}>-</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setAttemptsAllowed(prev => prev + 1)}>
                  <Text style={styles.numberButton}>+</Text>
                </TouchableOpacity>
              </View>

              <View style={styles.numberSelectorRow}>
                <Text style={styles.label}>Words per Session: {wordsPerSession}</Text>
                <TouchableOpacity onPress={() => setWordsPerSession(prev => Math.max(1, prev - 1))}>
                  <Text style={styles.numberButton}>-</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setWordsPerSession(prev => prev + 1)}>
                  <Text style={styles.numberButton}>+</Text>
                </TouchableOpacity>
              </View>

              <Button title="Start Speaking" onPress={() => { handleStartSession(); startListening(); }} />
            </View>
          </ScrollView>
        ) : !isSessionComplete ? (
          <View style={styles.gameContainer}>
            <View style={styles.headerContainer}>
              <Text style={styles.wordsRemaining}>Words Left: {wordsRemaining}</Text>
              <Text style={styles.attempts}>Attempts: {attempts}/{attemptsAllowed}</Text>
              <TouchableOpacity 
                style={styles.settingsButton} 
                onPress={() => setShowSettings(!showSettings)}
              >
                <Icon name="settings" type="material" color="#4CAF50" />
              </TouchableOpacity>
            </View>
            <View style={styles.wordContainer}>
              <Text style={styles.currentWord}>{currentWord}</Text>
              <TouchableOpacity onPress={speakWord} style={styles.speakerButton}>
                <Icon name="volume-up" type="font-awesome" size={24} color="#4CAF50" />
              </TouchableOpacity>
              
              <View style={styles.transcriptContainer}>
                <Text style={styles.interimText}>
                  {isListening ? (interimText || 'Listening...') : ''}
                </Text>
                <Text style={styles.recognizedText}>
                  {recognizedText ? `Last heard: ${recognizedText}` : ''}
                </Text>
              </View>
              
              <TouchableOpacity 
                onPress={handleStopSession}
                style={styles.stopButton}
              >
                <Icon name="stop-circle" type="font-awesome" size={24} color="#FF5252" />
                <Text style={styles.stopButtonText}>Stop Session</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : renderSessionSummary()}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  contentWrapper: {
    flex: 1,
  },
  splashScreen: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  splashContent: {
    alignItems: 'center',
  },
  splashTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
  },
  label: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  selectorContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  selectedButton: {
    backgroundColor: '#4CAF50',
  },
  unselectedButton: {
    backgroundColor: '#ccc',
  },
  selectorRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  levelButton: {
    padding: 10,
    borderWidth: 2,
    borderColor: '#4CAF50',
    borderRadius: 5,
  },
  selectedLevel: {
    backgroundColor: '#4CAF50',
  },
  levelText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  selectedLevelText: {
    color: '#fff',
  },
  numberSelectorRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  numberButton: {
    padding: 10,
    borderWidth: 2,
    borderColor: '#4CAF50',
    borderRadius: 5,
  },
  numberValue: {
    fontSize: 18,
    fontWeight: 'bold',
    marginHorizontal: 10,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  wordsRemaining: {
    fontSize: 18,
    color: '#666',
  },
  attempts: {
    fontSize: 18,
    color: '#666',
  },
  settingsButton: {
    padding: 8,
  },
  wordContainer: {
    flex: 1,
    alignItems: 'center',
    paddingTop: 20,
  },
  currentWord: {
    fontSize: 48,
    fontWeight: 'bold',
    marginBottom: 30,
    color: '#4CAF50',
  },
  gameContainer: {
    flex: 1,
  },
  speakerButton: {
    padding: 10,
  },
  transcriptContainer: {
    marginTop: 20,
  },
  interimText: {
    fontSize: 18,
  },
  recognizedText: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  sessionSummary: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  summaryTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
  },
  summaryScore: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  summaryText: {
    fontSize: 18,
    marginBottom: 10,
  },
  wordResultsContainer: {
    marginTop: 20,
    padding: 10,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  wordResult: {
    fontSize: 16,
    marginVertical: 2,
  },
  missedWordsContainer: {
    marginBottom: 20,
  },
  missedWordsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  missedWordsList: {
    fontSize: 18,
  },
  newSessionButton: {
    backgroundColor: '#4CAF50',
    paddingVertical: 15,
  },
  stopButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffebee',
    padding: 10,
    borderRadius: 20,
    marginTop: 20,
  },
  stopButtonText: {
    color: '#FF5252',
    marginLeft: 8,
    fontSize: 16,
    fontWeight: 'bold',
  },
  missedWordsHint: {
    fontSize: 14,
    color: '#666',
    fontStyle: 'italic',
    marginTop: 8,
  },
  buttonContainer: {
    marginTop: 20,
    gap: 10,
  },
  progressButton: {
    backgroundColor: '#2196F3',
    paddingVertical: 15,
  },
});